import { useEffect, useMemo } from 'react';
import {
  Flex,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/atoms/Button';
import { YapamarLogo } from 'src/components/atoms/YapamarLogo';
import { useAppContext } from 'src/contexts/AppContext';
import {
  ModelStatus,
  ModelType,
  useModelsListPolling,
  useModelStatusQuery,
} from 'src/api/models';
import { AppServices } from 'src/app/routes/utils';
import { ModelProgressBar } from './ModelProgressBar';

// Where each service's finished models live — used to send the user to the
// right dashboard when their model belongs to another service than the page
// they are currently on (e.g. onboarding lands on /mmm/* but the created
// model is Digital Attribution). Keyed by ModelType to forbid arbitrary
// string keys; note Partial means a widened union does NOT error here —
// when mo/lt land, their routes must be added by hand.
const SERVICE_HOME: Partial<
  Record<ModelType, { label: string; route: string }>
> = {
  mm: { label: 'Marketing Mix Modelling', route: '/mmm/overview' },
  da: { label: 'Digital Attribution', route: '/digital/overview' },
};

type InitialModelLoadingScreenProps = {
  currentService: AppServices | null;
};

const ScreenShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex
    flex="1"
    w="100%"
    minH="100vh"
    align="center"
    justify="center"
    px={{ base: 6, md: 12 }}
    py={{ base: 16, md: 24 }}>
    <VStack
      spacing={{ base: 4, md: 6 }}
      align="center"
      textAlign="center"
      w="100%"
      maxW="500px">
      {children}
    </VStack>
  </Flex>
);

export const InitialModelLoadingScreen: React.FC<
  InitialModelLoadingScreenProps
> = ({ currentService }) => {
  const { currentCustomer } = useAppContext();
  const navigate = useNavigate();
  const logoTheme = useColorModeValue('dark', 'white');
  const customerName = currentCustomer?.customerName ?? '';

  const { data: models, isLoading: isModelsLoading } =
    useModelsListPolling(customerName);

  // What to track, in priority order — the CURRENT page's service outranks
  // everything, so /mmm always tracks the mm model and /digital the da one:
  // 1. Current-service running QUICK.
  // 2. Current-service trackable running FULL (its own quick did not
  //    succeed — if the quick succeeded, the preview results are already
  //    live and nobody should stare at a day-long full bar).
  // 3. Any other running QUICK — only when this service has nothing of its
  //    own in flight (the onboarding case: landing on /mmm while a da
  //    model builds).
  // 4. Any other trackable FULL.
  // 5. Terminal states (ready here / ready elsewhere / failed / empty).
  const isInFlight = (status: ModelStatus) =>
    status === 'running' || status === 'not_started';

  // Pair identity: models are created as a Quick/Full pair sharing
  // modelName + createdAt (same key the backend and Settings use).
  const pairKey = (m: { modelName: string; createdAt: string }) =>
    `${m.createdAt}::${m.modelName}`;

  const runningModel = useMemo(() => {
    if (!models?.length) return null;

    const inFlightQuicks = models.filter(
      (m) => m.quickRun && isInFlight(m.status),
    );
    // Every in-flight full whose OWN quick did not succeed is trackable —
    // not just the first one the list happens to start with (multi-model
    // requests share one created_at, so list order between them is
    // arbitrary).
    const trackableFulls = models.filter(
      (full) =>
        !full.quickRun &&
        isInFlight(full.status) &&
        !models.some(
          (m) =>
            m.quickRun &&
            m.status === 'success' &&
            pairKey(m) === pairKey(full),
        ),
    );

    const ofCurrentService = <T extends { modelType?: ModelType }>(
      candidates: T[],
    ) => candidates.find((m) => m.modelType === currentService);

    return (
      ofCurrentService(inFlightQuicks) ??
      ofCurrentService(trackableFulls) ??
      inFlightQuicks[0] ??
      trackableFulls[0] ??
      null
    );
  }, [models, currentService]);

  const hasAnythingInFlight = runningModel !== null;

  const anySucceeded = models?.some((m) => m.status === 'success') ?? false;
  const allFailed =
    (models?.length ?? 0) > 0 && models!.every((m) => m.status === 'fail');

  // The dashboard gate (NavBarLayout) opens when the current service's
  // model-versions query returns data. The moment the models list — which
  // this screen polls every 5s anyway — reports a success for the current
  // service, force that versions query to refetch instead of relying on its
  // own interval, so the dashboard appears without a manual page refresh.
  const queryClient = useQueryClient();
  const readyForCurrentService =
    (models?.some(
      (m) =>
        m.status === 'success' &&
        (!m.modelType || m.modelType === currentService),
    ) ??
      false) &&
    Boolean(currentService);
  useEffect(() => {
    if (readyForCurrentService && customerName && currentService) {
      queryClient.invalidateQueries([
        'useModelVersionsQuery',
        customerName,
        currentService,
      ]);
    }
  }, [readyForCurrentService, customerName, currentService, queryClient]);

  // A finished model whose dashboard is NOT the current page's service —
  // the versions gate for this page will never unlock, so offer the switch.
  const readyElsewhere = useMemo(() => {
    if (!models?.length || hasAnythingInFlight) return null;
    const ready = models.find(
      (m) =>
        m.status === 'success' &&
        m.modelType &&
        m.modelType !== currentService &&
        SERVICE_HOME[m.modelType],
    );
    // Only divert when nothing succeeded for the CURRENT service either.
    // !m.modelType is deploy-skew tolerance: if the backend hasn't tagged
    // the model's service yet, assume it belongs to the current page rather
    // than diverting the user to a screen we can't verify is the right one.
    const readyHere = models.some(
      (m) =>
        m.status === 'success' &&
        (!m.modelType || m.modelType === currentService),
    );
    if (!ready || readyHere) return null;
    const home = SERVICE_HOME[ready.modelType!];
    if (!home) return null;
    // Pluralize the panel when several models of that service are done.
    const readyCount = models.filter(
      (m) => m.status === 'success' && m.modelType === ready.modelType,
    ).length;
    return { ...home, plural: readyCount > 1 };
  }, [models, hasAnythingInFlight, currentService]);

  const { data: modelStatus } = useModelStatusQuery(
    customerName,
    runningModel?.id ?? null,
  );

  const isRunning = modelStatus?.status === 'running';
  const hasFailed = modelStatus?.status === 'fail';

  // Still loading models list — show spinner
  if (isModelsLoading) {
    return (
      <Flex flex="1" w="100%" minH="100vh" align="center" justify="center">
        <Spinner size="lg" color="icon.green" />
      </Flex>
    );
  }

  if (!runningModel) {
    // A model finished for another service — its dashboard is ready there.
    if (readyElsewhere) {
      return (
        <ScreenShell>
          <Text fontSize="18px" fontWeight="600" color="content.title" mt={4}>
            {readyElsewhere.plural
              ? 'Your models are ready'
              : 'Your model is ready'}
          </Text>
          <Text fontSize="14px" color="content.muted" maxW="400px">
            The results live in {readyElsewhere.label} — this page shows a
            different service.
          </Text>
          <Button
            buttonName="go-to-ready-service"
            variant="primary"
            // The Button atom defaults to alignSelf="baseline", which breaks
            // the centered VStack layout — recenter explicitly.
            alignSelf="center"
            onClick={() => navigate(readyElsewhere.route)}>
            Open {readyElsewhere.label}
          </Button>
        </ScreenShell>
      );
    }

    // A model for this service just finished — its published results appear
    // within seconds (the versions query polls); show a finalizing state
    // instead of flashing "No models yet".
    if (anySucceeded) {
      return (
        <ScreenShell>
          <Spinner size="lg" color="icon.green" />
          <Text fontSize="18px" fontWeight="600" color="content.title" mt={4}>
            Finalizing your dashboard...
          </Text>
          <Text fontSize="14px" color="content.muted" maxW="360px">
            Your model finished — publishing the results now. This only takes a
            moment.
          </Text>
        </ScreenShell>
      );
    }

    if (allFailed) {
      return (
        <ScreenShell>
          <Text fontSize="18px" fontWeight="600" color="content.title" mt={4}>
            Modeling failed
          </Text>
          <Text fontSize="14px" color="content.muted" maxW="400px">
            Your model run did not complete. Head to Settings to review its
            configuration and re-run it, or contact support.
          </Text>
        </ScreenShell>
      );
    }

    // Genuinely nothing here yet.
    return (
      <ScreenShell>
        <Text fontSize="18px" fontWeight="600" color="content.title" mt={4}>
          No models yet
        </Text>
        <Text fontSize="14px" color="content.muted" maxW="360px">
          Create your first model to start seeing insights. Head to Settings to
          get started.
        </Text>
      </ScreenShell>
    );
  }

  // Model in progress — show progress bar
  const phaseLabel =
    runningModel.modelType === 'lt'
      ? 'Long-term model'
      : runningModel.quickRun
        ? 'Quick preview'
        : 'Full model';
  const statusMessage = hasFailed
    ? 'Modeling encountered an error. Please check the details below.'
    : isRunning
      ? "Modeling in progress. We're building your dashboard..."
      : 'Preparing your models...';

  return (
    <Flex
      flex="1"
      w="100%"
      minH="100vh"
      align="center"
      justify="center"
      px={{ base: 6, md: 12 }}
      py={{ base: 16, md: 24 }}>
      <VStack
        spacing={{ base: 6, md: 8 }}
        align="center"
        textAlign="center"
        w="100%"
        maxW="500px">
        <YapamarLogo theme={logoTheme} clickable={false} />

        <VStack spacing={6} w="100%" px={4}>
          <ModelProgressBar
            label={`${runningModel.modelName ?? 'Model'} — ${phaseLabel}`}
            status={modelStatus ?? null}
          />
        </VStack>

        <Text
          fontSize="16px"
          fontWeight="500"
          color="content.title"
          maxW="400px">
          {statusMessage}
        </Text>

        {!hasFailed && (
          <Text fontSize="13px" color="content.muted" maxW="360px">
            You can close this page and come back later. We&apos;ll keep
            processing your models.
          </Text>
        )}
      </VStack>
    </Flex>
  );
};
